<script>
import Layout from '@layouts/main.vue'
import DeleteAccountModal from '@components/modals/DeleteAccountModal/DeleteAccountModal.vue'
import { mapActions, mapState } from 'vuex'
import validationMixin from '@components/mixins/validation'

export default {
  page: {
    title: 'User Details',
    meta: [{ name: 'description', content: 'Details about the user' }],
  },
  name: 'UserDetailsView',
  components: { Layout, 'delete-account-modal-component': DeleteAccountModal },
  mixins: [validationMixin],
  data() {
    const checkValidUser = (rule, value, callback) => {
      callback(this.validateUser(rule, value, callback))
    }

    const checkValidEmail = (rule, value, callback) => {
      callback(this.validateEmail(rule, value, callback))
    }

    const checkValidFirstName = (rule, value, callback) => {
      callback(this.validateFirstName(rule, value, callback))
    }

    const checkValidLastName = (rule, value, callback) => {
      callback(this.validateLastName(rule, value, callback))
    }

    return {
      userDetailsForm: {
        username: '',
        firstName: '',
        lastName: '',
        email: '',
        timezone: '',
      },
      rules: {
        username: [{ validator: checkValidUser, trigger: 'blur' }],
        email: [{ validator: checkValidEmail, trigger: 'blur' }],
        firstName: [{ validator: checkValidFirstName, trigger: 'blur' }],
        lastName: [{ validator: checkValidLastName, trigger: 'blur' }],
        timezone: {},
      },
      confirmationResend: false,
      disableSaveBtn: false,
      deleteAccountModalVisible: false,
    }
  },
  computed: {
    ...mapState({
      getUserCompleted: (state) => state.users.getUserCompleted,
      userDetails: (state) => state.users.currentUser,
      updateCompleted: (state) => state.users.updateCompleted,
      updateError: (state) => state.users.updateError,
      updateLoading: (state) => state.users.updateLoading,
      updateErrorMessage: (state) => state.users.updateErrorMessage,
    }),
  },
  watch: {
    userDetails: {
      handler() {
        this.setUserDetails()
      },
      deep: true,
    },
    updateCompleted(value) {
      if (value) {
        this.$message({
          type: 'success',
          message: 'Account has been successfully updated!.',
          duration: 2000
        })
      }
    },
  },
  created() {
    this.setUserDetails()
  },
  methods: {
    ...mapActions('users', [
      'updateAccountDetails',
      'clearUpdateAccountStatus',
    ]),
    formSubmit(form) {
      this.disableSaveBtn = true
      this.$refs.userDetailsForm.validate((valid) => {
        if (valid) {
          // ToDo Display message returned from server (e.g. username already exists)
          this.updateAccountDetails(form).then(() => {
            setTimeout(() => {
              this.disableSaveBtn = false
            }, 700)
          })
        }
      })
    },
    resendConfirmation() {
      this.$store.dispatch('auth/resendConfirmationEmail').then((response) => {
        if (response.data) {
          this.confirmationResend = true
        }
      })
    },
    setUserDetails() {
      this.userDetailsForm.username = this.userDetails.username
      this.userDetailsForm.email = this.userDetails.email
      this.userDetailsForm.firstName = this.userDetails.firstName
      this.userDetailsForm.lastName = this.userDetails.lastName
      this.userDetailsForm.timezone = this.userDetails.timezone
    },
    showDeleteAccountModal() {
      this.deleteAccountModalVisible = true
    },
  },
}
</script>

<template>
  <Layout>
    <div :class="$style.userDetailsView">
      <div class="container">
        <section v-if="getUserCompleted" class="mb-10">
          <el-form
              ref="userDetailsForm"
              :model="userDetailsForm"
              class="single-column"
              :rules="rules"
              :disabled="updateLoading"
              label-width="85px"
              label-position="top"
              status-icon
              @submit.native.prevent>
            <el-form-item
                label="Username*"
                prop="username"
                maxlength="5">
              <el-input
                  v-model="userDetailsForm.username"
                  type="text"
                  maxlength="140"
                  auto-complete="off"></el-input>
            </el-form-item>
            <el-form-item label="Email*" prop="email">
              <el-input
                  v-model="userDetailsForm.email"
                  type="text"
                  maxlength="140"
                  :disabled="true"></el-input>
              <span
                  v-if="!userDetails.isConfirmed"
                  :class="[
                  $style.resendConfirmation,
                  { [$style.sent]: confirmationResend },
                ]"
                  @click="resendConfirmation">
                {{
                  confirmationResend
                      ? 'Confirmation Email Sent'
                      : 'Resend Confirmation Email'
                }}
              </span>
            </el-form-item>
            <el-form-item label="First name" prop="firstName">
              <el-input
                  v-model="userDetailsForm.firstName"
                  type="text"
                  maxlength="140"></el-input>
            </el-form-item>
            <el-form-item label="Last name" prop="lastName">
              <el-input
                  v-model="userDetailsForm.lastName"
                  type="text"
                  maxlength="140"></el-input>
            </el-form-item>
            <el-form-item label="Time zone" prop="timezone">
              <el-select v-model="userDetailsForm.timezone" disabled style="width: 100%">

              </el-select>
            </el-form-item>
            <el-form-item>
              <button class="btn primary"
                      type="button"
                      :disabled="updateLoading || disableSaveBtn"
                      @click="formSubmit(userDetailsForm)">Update
                Account
              </button>
            </el-form-item>
          </el-form>

          <span v-if="updateError" class="text-onvocado-red">
            {{ updateErrorMessage }}
          </span>

        </section>

        <template v-else>
          <Loader></Loader>
        </template>

        <div class="border mb-12"></div>

        <section>
          <button class="btn danger" type="button" @click="showDeleteAccountModal">Delete account</button>
        </section>
      </div>
    </div>
    <delete-account-modal-component :delete-account-modal-visible="deleteAccountModalVisible"
                                   @close-modal="deleteAccountModalVisible = false"/>
  </Layout>
</template>

<style lang="scss" module>
.userDetailsView {
  display: flex;
  height: 100%;
}

.resendConfirmation {
  @apply underline cursor-pointer;
}

.sent {
  @apply text-onvocado-green;
  text-decoration: none;
}
</style>
